import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SortableTable from '~/components/core/Tables/SortableTable';
import { Text } from '~/components/core/TextComponents';
import { PencilIcon, TrashIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import PolicyCategoryDialog from '~/components/SystemConfiguration/PolicyConfiguration/PolicyCategories/PolicyCategoryDialog';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Switch from '~/components/ui/atoms/Switch';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateToLocal } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import type { GlobalCategoryRow } from './types';
import { globalCategoriesUrl } from './types';

/**
 * Global categories: organization-wide groupings a global FNOL field can be limited to.
 *
 * `SortableTable` rather than the drag-ordered list the two field tabs use, following the
 * policy-categories sibling: categories carry no order. Every matching category applies at once,
 * which is why the backend table has no priority column.
 */
const GlobalCategoriesTab: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore useSysconfig is an untyped JSX module.
  const { organization } = useSysconfig();
  const baseUrl = globalCategoriesUrl(organization.id);

  const { isLoading, isError, data: categories, reloadData } = useDataFetcher<GlobalCategoryRow[]>(baseUrl);

  const [searchText, setSearchText] = useState('');
  const [dialogState, setDialogState] = useState<{ open: boolean; category: GlobalCategoryRow | null }>({
    open: false,
    category: null,
  });
  const [categoryToDelete, setCategoryToDelete] = useState<GlobalCategoryRow | null>(null);
  const [categoryToDisable, setCategoryToDisable] = useState<GlobalCategoryRow | null>(null);
  // Rows with a PATCH in flight. Without this a second click issues a second PATCH whose
  // completion order — not the last click — decides the stored value.
  const [pendingCategoryIds, setPendingCategoryIds] = useState<ReadonlySet<number>>(new Set());

  const filteredCategories = (categories || []).filter(
    ({ display_name, key }) =>
      display_name.toLowerCase().includes(searchText.toLowerCase()) ||
      key.toLowerCase().includes(searchText.toLowerCase())
  );

  const patchEnabled = async (category: GlobalCategoryRow, isEnabled: boolean) => {
    setPendingCategoryIds((pending) => new Set(pending).add(category.id));
    try {
      await axios.patch(`${baseUrl}/${category.id}`, { is_enabled: isEnabled });
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setPendingCategoryIds((pending) => {
        const next = new Set(pending);
        next.delete(category.id);
        return next;
      });
    }
  };

  const handleToggleEnabled = async (category: GlobalCategoryRow) => {
    // Disabling a category a global field is limited to hides that field on every form in the
    // organization — confirm rather than flip quietly. Enabling has no such blast radius.
    if (category.is_enabled && category.is_in_use) {
      setCategoryToDisable(category);
      return;
    }
    await patchEnabled(category, !category.is_enabled);
  };

  const handleConfirmedDisable = async () => {
    if (!categoryToDisable) return;
    const category = categoryToDisable;
    setCategoryToDisable(null);
    await patchEnabled(category, false);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    // Close before awaiting: ConfirmModal leaves its buttons live unless the caller drives
    // `isSubmitting`, so a modal still open across the request takes a second click and fires a
    // second DELETE — which 404s on the now-removed row and reports an error for a delete that
    // succeeded.
    const category = categoryToDelete;
    setCategoryToDelete(null);
    try {
      await axios.delete(`${baseUrl}/${category.id}`);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const columns = [
    { id: 'display_name', label: 'Name' },
    { id: 'key', label: 'Key' },
    {
      id: 'last_updated_on',
      label: 'Last Updated',
      specialCell: ({ last_updated_on }: GlobalCategoryRow) => serverDateToLocal(last_updated_on),
    },
    { id: 'updated_by', label: 'Updated By' },
    {
      id: 'is_enabled',
      label: 'Enable',
      disableSort: true,
      specialCell: (row: GlobalCategoryRow) => (
        <Switch
          // Named after the category: a positional label follows whichever row sits at that index.
          aria-label={`Enable — ${row.display_name}`}
          checked={row.is_enabled}
          disabled={pendingCategoryIds.has(row.id)}
          onChange={() => handleToggleEnabled(row)}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      width: '90px',
      specialCell: (row: GlobalCategoryRow) => (
        <div className="flex flex-row">
          <InlineIconButton
            icon={PencilIcon}
            tooltipTitle="Edit"
            // @ts-expect-error InlineIconButton is an untyped JSX module, so its optional props are
            // inferred as the `undefined` defaults it destructures. Fix the types when it is converted.
            ariaLabel={`Edit category ${row.display_name}`}
            onClick={() => setDialogState({ open: true, category: row })}
          />
          <InlineIconButton
            icon={TrashIcon}
            // The BE refuses a delete while a global field's limit list names the key; disable up
            // front rather than letting the click discover the rejection.
            disabled={row.is_in_use}
            tooltipTitle={
              row.is_in_use
                ? 'This category is in use by a global field. Remove the references first, or disable it.'
                : 'Delete'
            }
            // @ts-expect-error see the `ariaLabel` note above.
            ariaLabel={`Delete category ${row.display_name}`}
            onClick={() => setCategoryToDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <CardDialog noCardTitle>
        <Text variant="small" weight="regular">
          Global categories are organization-wide groupings defined by a condition over policy fields. Limit a global
          FNOL field to one so it appears only when the claim&apos;s policy matches.
        </Text>
        <div className="flex flex-col gap-16">
          <div className="my-4 w-1/4">
            <TextField
              fullWidth
              value={searchText}
              onChange={(value: string) => setSearchText(value)}
              placeholder="Search Categories"
            />
          </div>
          <div className="flex justify-between">
            <Text weight={Text.WEIGHTS.SEMI_BOLD} variant={Text.VARIANTS.SM}>
              {`${filteredCategories.length} ${filteredCategories.length === 1 ? 'Category' : 'Categories'}`}
            </Text>
            <Button color="primary" onClick={() => setDialogState({ open: true, category: null })}>
              + Add Category
            </Button>
          </div>
        </div>
        <LoadingSwitch isLoading={isLoading} isError={isError}>
          <SortableTable columns={columns} rows={filteredCategories} stickyHeader maxHeight="60vh" />
        </LoadingSwitch>
      </CardDialog>

      {dialogState.open && (
        <PolicyCategoryDialog
          categoriesUrl={baseUrl}
          entityLabel="Global Category"
          emptyCatalogMessage="No global policy fields available. Add a Policy field first to condition on it."
          category={dialogState.category}
          // `other` is the configuration blob's "no category" sentinel, which the global categories
          // manager refuses as a key.
          reservedKeys={['other']}
          onClose={() => setDialogState({ open: false, category: null })}
          onSaved={reloadData}
        />
      )}

      <ConfirmModal
        isOpen={!!categoryToDelete}
        title="Delete Global Category?"
        contentText={`The category "${categoryToDelete?.display_name ?? ''}" will be deleted. This cannot be undone.`}
        primaryButtonName="Delete"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleDelete}
        onClose={() => setCategoryToDelete(null)}
      />

      <ConfirmModal
        isOpen={!!categoryToDisable}
        title="Disable Global Category?"
        contentText={`"${categoryToDisable?.display_name ?? ''}" is referenced by a global field. Disabling it will hide every field limited to it, on every form in the organization, until it is enabled again.`}
        primaryButtonName="Disable"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleConfirmedDisable}
        onClose={() => setCategoryToDisable(null)}
      />
    </>
  );
};

export default GlobalCategoriesTab;
